/**
 * Which state forms the staff "Regenerate Policy Form" tool can regenerate for a policy (#36559).
 *
 * The tool used to list every form, so only agents who knew each form's state could use it. These
 * rules narrow the list to the policy's state and line, mirroring where each form is generated:
 * the bind application packet (docs-engine docs-handler/application-handler.js), endorsements
 * (staff-appsync-be policy-mod segments.manager / confirm-policy.event) and the pending-docs guards.
 *
 * Deliberately state + line only — not the coverage conditions bind also applies (e.g. CODW only on a
 * $100/$250 collision deductible), because staff regenerate a form precisely when that coverage just
 * changed. Where bind and endorsement disagree, the broadest real generation path wins: confirm-policy
 * regenerates the UM and NDEL forms in any auto state, so those are offered everywhere.
 *
 * Branch paper only. Mutual brands (CFM, Forreston, Oakwood) produce these forms through other
 * pipelines, and the tool leaves their list unfiltered — callers check the brand, not this module.
 */
import { documentTypes, offerExcessUM, policyTypeLines } from './constants';

type PolicyFormStates = { states: readonly string[] } | { allStatesExcept: readonly string[] };

/** The policy lines these forms attach to: A = auto, H = home, C = condo. */
type PolicyFormLine = 'A' | 'H' | 'C';

const ALL_STATES: PolicyFormStates = { allStatesExcept: [] };

const excessUMStates = Object.keys(offerExcessUM).filter((state) => offerExcessUM[state as keyof typeof offerExcessUM]);

const regenerablePolicyFormRules: Record<number, Partial<Record<PolicyFormLine, PolicyFormStates>>> = {
  [documentTypes.UM_NOTICE]: { A: ALL_STATES },
  [documentTypes.MEDPAY]: { A: { states: ['CO', 'WI'] } },
  [documentTypes.PIP_NOTICE]: { A: { states: ['AR', 'MD'] } },
  [documentTypes.NDEL_NOTICE]: { A: ALL_STATES },
  [documentTypes.PIP_SELECTION_FORM]: { A: { states: ['MI'] } },
  [documentTypes.MPSF]: { A: { states: ['MI'] } },
  [documentTypes.WORK_LOSS_BENEFITS_WAIVER]: { A: { states: ['MI'] } },
  [documentTypes.KY_NO_FAULT_REJECTION]: { A: { states: ['KY'] } },
  [documentTypes.TORT_SELECTION]: { A: { states: ['PA'] } },
  // Umbrella-only, so it rides the property policy, not the auto.
  [documentTypes.EXCESS_UM_UIM_SELECTION_REJECTION_FORM]: {
    H: { states: excessUMStates },
    C: { states: excessUMStates }
  },
  // MA has no named driver exclusion (be-constants docsSettings noNDEXStates).
  [documentTypes.NAMED_DRIVER_EXCLUSION]: { A: { allStatesExcept: ['MA'] } },
  [documentTypes.LOUISIANA_DEDUCTIBLE_NOTICE]: { H: { states: ['LA'] } },
  // H_NOTC is only generated in SC, which isn't live today — kept so the tool matches bind if it is.
  [documentTypes.RATING_INFORMATION_NOTICE]: { H: { states: ['SC'] } },
  // OH: coverage bought (home or condo). IL: coverage declined in a defaulted county (home only).
  [documentTypes.MINE_SUBSIDENCE]: { H: { states: ['OH', 'IL'] }, C: { states: ['OH'] } },
  [documentTypes.MINE_SUBSIDENCE_SELECTION_REJECTION]: { H: { states: ['IN', 'KY'] } },
  [documentTypes.COAL_MINE_SUBSIDENCE_WAIVER]: { H: { states: ['WV'] } },
  [documentTypes.LOSS_OF_INCOME_WAIVER]: { A: { states: ['UT'] } },
  [documentTypes.COLLISION_DEDUCTIBLE_WAIVER]: { A: { states: ['PA'] } }
};

/** Every doc type the Regenerate Policy Form tool offers, ascending. */
export const regenerablePolicyFormDocTypes: number[] = Object.keys(regenerablePolicyFormRules)
  .map(Number)
  .sort((a, b) => a - b);

/** True for the doc types the tool offers; other regeneratePolicyForm callers (cancellation, etc.) are not. */
export const isRegenerablePolicyForm = (docType: number): boolean => docType in regenerablePolicyFormRules;

/** True when the form is generated for a policy of this state and type (bundles count as each line). */
export const isPolicyFormApplicable = ({
  docType,
  state,
  policyType
}: {
  docType: number;
  state?: string | null;
  policyType?: string | null;
}): boolean => {
  const rulesByLine = regenerablePolicyFormRules[docType];
  if (!rulesByLine || !state || !policyType) {
    return false;
  }
  const lines = policyTypeLines[policyType] ?? [policyType];
  return lines.some((line) => {
    const rule = rulesByLine[line as PolicyFormLine];
    if (!rule) {
      return false;
    }
    return 'states' in rule ? rule.states.includes(state) : !rule.allStatesExcept.includes(state);
  });
};

/** The tool's doc types that apply to a policy of this state and type, ascending. */
export const getApplicablePolicyFormDocTypes = ({
  state,
  policyType
}: {
  state?: string | null;
  policyType?: string | null;
}): number[] =>
  regenerablePolicyFormDocTypes.filter((docType) => isPolicyFormApplicable({ docType, state, policyType }));
